/**
 * Data bisnis Cipta Imaji yang ditampilkan ke publik.
 *
 * Nilai null berarti BELUM DIISI oleh tim CI: bagian halaman yang bergantung
 * padanya tidak dirender sama sekali, bukan menampilkan teks placeholder.
 * Tinggal di paket shared karena dibaca dua pihak: halaman web (kontak,
 * WhatsApp, JSON-LD) dan Imma AI di API. Keduanya harus menjawab dari sumber
 * yang sama, kalau tidak Imma bisa menyebut nomor yang tidak ada di situs.
 */
export const COMPANY = {
  name: 'Cipta Imaji',
  /** Nama badan usaha untuk kebijakan/legal, mis. "CV Cipta Imaji". */
  legalName: 'PT. Cipta Imaji Kreasindo' as string | null,
  /**
   * Nomor WhatsApp dalam format internasional tanpa +, mis. "628123456789". Diisi lewat env
   * (API `COMPANY_WHATSAPP`, web `NEXT_PUBLIC_COMPANY_WHATSAPP`) lewat companyWithContacts.
   */
  whatsapp: null as string | null,
  /** Diisi lewat env `COMPANY_EMAIL` / `NEXT_PUBLIC_COMPANY_EMAIL`. */
  email: null as string | null,
  /** Baris alamat workshop, satu elemen per baris. */
  address: [
    'Jl. Pelajar Pejuang 45 No.9, Lkr. Selatan',
    'Kec. Lengkong, Kota Bandung',
    'Jawa Barat 40264',
  ] as string[] | null,
  /** Tautan Google Maps ke workshop. */
  mapsUrl:
    'https://www.google.com/maps/search/?api=1&query=-6.925754749446935,107.62768978466039' as
      string | null,
  /** Jam operasional, satu elemen per baris. Diisi lewat env `COMPANY_HOURS` (dipisah "|"). */
  hours: null as string[] | null,
} as const;

export type CompanyInfo = {
  -readonly [K in keyof typeof COMPANY]: (typeof COMPANY)[K];
};

/** Kontak yang diisi lewat env (fase B go-live), bukan di kode. */
export interface CompanyContactEnv {
  /** Nomor WA; boleh "+62 812-3456-789" atau "0812…", dirapikan jadi "62812…". */
  whatsapp?: string | undefined;
  email?: string | undefined;
  /** Jam buka, baris dipisah "|", mis. "Senin–Jumat 09.00–17.00|Sabtu 09.00–14.00". */
  hours?: string | undefined;
}

/**
 * Nomor WA ke format wa.me (kode negara tanpa +). Nomor yang tidak masuk akal ditolak
 * dengan galat, supaya salah isi env ketahuan saat start, bukan saat pelanggan menekan
 * tombol WhatsApp.
 */
export function normalizeWhatsapp(raw: string): string {
  let digits = raw.replace(/[\s().-]/g, '').replace(/^\+/, '');
  if (digits.startsWith('0')) digits = `62${digits.slice(1)}`;
  if (!/^62\d{8,13}$/.test(digits)) {
    throw new Error(`Nomor WhatsApp tidak sah: "${raw}" (contoh: 6281234567890)`);
  }
  return digits;
}

/**
 * Data perusahaan lengkap: nilai dasar di atas + kontak dari env. Kosong = null, jadi
 * bagian halaman yang bergantung padanya tetap tidak dirender sampai CI memberi datanya.
 */
export function companyWithContacts(contacts: CompanyContactEnv): CompanyInfo {
  const text = (value: string | undefined) => (value && value.trim() ? value.trim() : null);
  const whatsapp = text(contacts.whatsapp);
  const email = text(contacts.email);
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    throw new Error(`Email perusahaan tidak sah: "${email}"`);
  }
  const hours = text(contacts.hours)
    ?.split('|')
    .map((line) => line.trim())
    .filter(Boolean);
  return {
    ...COMPANY,
    whatsapp: whatsapp ? normalizeWhatsapp(whatsapp) : null,
    email,
    hours: hours && hours.length > 0 ? hours : null,
  };
}

export function whatsappUrlFor(company: { whatsapp: string | null }, text?: string): string | null {
  if (!company.whatsapp) return null;
  const q = text ? `?text=${encodeURIComponent(text)}` : '';
  return `https://wa.me/${company.whatsapp}${q}`;
}

/** @deprecated Selalu null sejak kontak pindah ke env; pakai `whatsappUrlFor(company)`. */
export function whatsappUrl(text?: string): string | null {
  return whatsappUrlFor(COMPANY, text);
}
